<template>
  <Teleport to="body">
    <div class="wo-drawer" :class="{ 'wo-drawer--open': isVisible }">
      <div class="wo-drawer__overlay"></div>

      <div class="wo-drawer__panel">
        <!-- Mobile handle -->
        <div class="wo-drawer__handle" @touchstart="onTouchStart" @touchmove="onTouchMove">
          <div class="wo-drawer__handle-bar"></div>
        </div>

        <!-- Header -->
        <div class="wo-drawer__header">
          <button class="wo-drawer__back" @click="$emit('close')">
            <BaseIcon name="chevron" size="14" rotate="270" />
            Terug
          </button>
          <div class="wo-drawer__header-right">
            <button
              v-if="canCancelWorkOrder"
              type="button"
              class="wo-drawer__cancel-link"
              :disabled="cancellingWorkOrder"
              @click.stop="handleCancelWorkOrder"
            >
              Annuleren
              <BaseIcon name="xmark" size="12" />
            </button>
            <router-link
              v-if="canOpenProject"
              :to="`/projects/${linkedProject.id}`"
              class="wo-drawer__edit-link"
              @click.stop="$emit('close')"
            >
              {{ $t('projects.view_project') }}
              <BaseIcon name="arrow-up-right" size="12" />
            </router-link>
            <router-link
              v-if="!installerMode || isAdmin"
              :to="`/work-orders/${workOrder.id}`"
              class="wo-drawer__edit-link"
              @click.stop="$emit('close')"
            >
              Wijzigen
              <BaseIcon name="external-link" size="12" />
            </router-link>
            <span class="wo-drawer__status" :class="'wo-drawer__status--' + workOrder.status">
              · {{ detail.getStatusLabel(workOrder.status) }}
            </span>
          </div>
        </div>

        <!-- Title block -->
        <div class="wo-drawer__title-block">
          <div class="wo-drawer__wo-number-row">
            <span class="wo-drawer__wo-number">{{ workOrder.work_order_number }}</span>
            <select
              v-if="canChangeType"
              class="wo-drawer__type-badge wo-drawer__type-select"
              :class="`wo-drawer__type-badge--${workOrder.work_order_type || 'installation'}`"
              :value="workOrder.work_order_type || 'installation'"
              :disabled="changingType"
              :aria-label="$t('work_orders.type_field')"
              @click.stop
              @change="handleTypeChange"
            >
              <option v-for="type in workOrderTypes" :key="type" :value="type">
                {{ $t(`work_orders.types.${type}`) }}
              </option>
            </select>
            <span
              v-else-if="workOrder.work_order_type && workOrder.work_order_type !== 'installation'"
              class="wo-drawer__type-badge"
              :class="`wo-drawer__type-badge--${workOrder.work_order_type}`"
            >
              {{ $t(`work_orders.types.${workOrder.work_order_type}`) }}
            </span>
          </div>
          <h2 class="wo-drawer__project-title">
            {{ drawerTitle }}
          </h2>
          <div class="wo-drawer__quick-actions" v-if="workOrder.status !== 'completed'">
            <Btn v-if="customerPhone" variant="outlined" size="sm" icon="phone" @click.stop="call">
              Bel
            </Btn>
            <Btn
              v-if="detail.navigationLink.value"
              variant="outlined"
              size="sm"
              icon="map-pin"
              @click.stop="navigate"
            >
              Navigeer
            </Btn>
            <Btn
              v-if="installerMode && detail.canStart.value"
              variant="primary"
              size="sm"
              icon="play"
              @click.stop="handleStartWorkOrder"
            >
              Start
            </Btn>
          </div>
        </div>

        <!-- Scrollable body -->
        <div class="wo-drawer__body">
          <div v-if="!installerMode" class="wo-drawer__notes-edit">
            <TextAreaInput
              id="wo_drawer_installer_notes"
              :label="$t('work_orders.installer_notes')"
              v-model="detail.installerNotes.value"
              placeholder="Zichtbaar bovenaan de werkbon voor de monteur..."
              :rows="3"
              @blur="detail.saveInstallerNotes"
            />
          </div>
          <MessageElement v-else-if="workOrder.installer_notes" class="wo-drawer__notes">
            {{ workOrder.installer_notes }}
          </MessageElement>

          <!-- Map -->
          <div class="wo-drawer__map" v-if="detail.mapUrl.value">
            <iframe
              :src="detail.mapUrl.value"
              width="100%"
              height="160"
              style="border: 0; display: block"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
            <a
              v-if="detail.googleMapsLink.value"
              :href="detail.googleMapsLink.value"
              target="_blank"
              class="wo-drawer__map-chip"
            >
              <BaseIcon name="external-link" size="11" />
              Maps
            </a>
          </div>

          <!-- Address -->
          <div class="wo-drawer__address" v-if="detail.installationAddress.value">
            <div class="wo-drawer__address-left">
              <BaseIcon name="map-marker" size="13" />
              <div>
                <p class="wo-drawer__address-street">
                  {{ detail.installationAddress.value.street }}
                  {{
                    detail.installationAddress.value.house_number ||
                    detail.installationAddress.value.houseNumber
                  }}
                </p>
                <p class="wo-drawer__address-city">
                  {{ detail.installationAddress.value.city }}
                </p>
              </div>
            </div>
            <a
              v-if="detail.navigationLink.value"
              :href="detail.navigationLink.value"
              target="_blank"
              class="wo-drawer__nav-btn"
            >
              <BaseIcon name="chevron" size="11" rotate="90" />
              Navigeer
            </a>
          </div>

          <!-- Info rows -->
          <div class="wo-drawer__info-list">
            <div class="wo-drawer__info-row" v-if="detail.appointmentDate.value">
              <span class="wo-drawer__info-label">Datum</span>
              <span class="wo-drawer__info-value">
                {{ detail.formatDate(detail.appointmentDate.value) }}
                · {{ detail.formatTime(detail.appointmentDate.value) }}
                <template v-if="endTime">–{{ endTime }}</template>
              </span>
            </div>
            <div class="wo-drawer__info-row" v-if="detail.customer.value">
              <span class="wo-drawer__info-label">Klant</span>
              <span class="wo-drawer__info-value">{{ detail.customer.value.name }}</span>
            </div>
            <div class="wo-drawer__info-row" v-if="canOpenProject">
              <span class="wo-drawer__info-label">Project</span>
              <router-link
                :to="`/projects/${linkedProject.id}`"
                class="wo-drawer__info-link"
                @click.stop="$emit('close')"
              >
                {{ linkedProject.label }}
              </router-link>
            </div>
            <div class="wo-drawer__info-row" v-if="salesperson">
              <span class="wo-drawer__info-label">{{ $t('projects.salesperson') }}</span>
              <span class="wo-drawer__info-value wo-drawer__info-value--with-action">
                {{ salesperson.name }}
                <a
                  v-if="salesperson.email"
                  :href="`mailto:${salesperson.email}`"
                  class="wo-drawer__info-link"
                  :title="salesperson.email"
                >
                  <BaseIcon name="envelope" size="14" />
                </a>
              </span>
            </div>
            <div class="wo-drawer__info-row" v-if="customerPhone">
              <span class="wo-drawer__info-label">Contact</span>
              <a :href="'tel:' + customerPhone" class="wo-drawer__info-link">
                {{ customerPhone }}
              </a>
            </div>
            <div class="wo-drawer__info-row" v-if="!installerMode">
              <span class="wo-drawer__info-label">Monteur</span>
              <span class="wo-drawer__info-value">{{ detail.installerName.value }}</span>
            </div>
          </div>

          <!-- ═══ Collapsible sections ═══ -->

          <!-- Activiteiten -->
          <div class="wo-drawer__accordion">
            <button
              class="wo-drawer__accordion-hd"
              @click="sections.activities = !sections.activities"
            >
              <div class="wo-drawer__accordion-title">
                <BaseIcon name="list" size="15" />
                <span>Activiteiten</span>
              </div>
              <div class="wo-drawer__accordion-right">
                <button
                  v-if="sections.activities"
                  class="wo-drawer__inline-btn"
                  @click.stop="detail.addActivity"
                >
                  + Activiteit
                </button>
                <BaseIcon
                  name="chevron"
                  size="12"
                  :rotate="sections.activities ? 180 : 0"
                  class="wo-drawer__accordion-chevron"
                />
              </div>
            </button>
            <div class="wo-drawer__accordion-bd" v-if="sections.activities">
              <div v-if="detail.activitiesLoading.value" class="wo-drawer__loading">
                <LoadingSpinner size="sm" />
              </div>
              <div v-else-if="detail.activities.value.length > 0" class="wo-drawer__act-list">
                <div
                  v-for="(activity, index) in detail.activities.value"
                  :key="activity.id"
                  class="wo-drawer__act-item"
                  :class="{ 'wo-drawer__act-item--done': activity.logged_minutes !== null }"
                >
                  <input
                    type="checkbox"
                    :checked="activity.logged_minutes !== null"
                    @change="activity.logged_minutes !== null ? null : detail.openLogTime(activity)"
                    class="wo-drawer__act-check"
                  />
                  <span class="wo-drawer__act-name" @click="detail.editActivity(activity)">
                    <input
                      v-if="activity.editing"
                      v-model="activity.name"
                      class="wo-drawer__act-input"
                      @blur="detail.saveActivity(activity)"
                      @keyup.enter="detail.saveActivity(activity)"
                    />
                    <template v-else>{{ activity.name }}</template>
                  </span>
                </div>
              </div>
              <div v-else class="wo-drawer__empty">
                <p>Nog geen activiteiten</p>
                <button class="wo-drawer__empty-action" @click="detail.addDefaultActivities">
                  Standaard activiteiten toevoegen
                </button>
              </div>
            </div>
          </div>

          <!-- Pakbon -->
          <div class="wo-drawer__accordion">
            <button class="wo-drawer__accordion-hd" @click="sections.pakbon = !sections.pakbon">
              <div class="wo-drawer__accordion-title">
                <BaseIcon name="package" size="15" />
                <span>Pakbon</span>
              </div>
              <BaseIcon
                name="chevron"
                size="12"
                :rotate="sections.pakbon ? 180 : 0"
                class="wo-drawer__accordion-chevron"
              />
            </button>
            <div class="wo-drawer__accordion-bd" v-if="sections.pakbon">
              <div v-if="detail.packingSlipLoading.value" class="wo-drawer__loading">
                <LoadingSpinner size="sm" />
              </div>
              <template v-else-if="detail.packingSlipData.value">
                <div
                  v-for="(item, idx) in detail.packingSlipData.value.items"
                  :key="idx"
                  class="wo-drawer__slip-item"
                  :class="{ 'wo-drawer__slip-item--done': detail.packingSlipChecked.value[idx] }"
                >
                  <!-- Header row: checkbox + name + qty + toggle -->
                  <div class="wo-drawer__slip-row">
                    <input
                      type="checkbox"
                      :checked="detail.packingSlipChecked.value[idx]"
                      @change="detail.togglePackingSlipItem(idx)"
                      class="wo-drawer__slip-check"
                    />
                    <span class="wo-drawer__slip-label" @click="toggleSlipItem(idx)">
                      {{ item.label }}
                    </span>
                    <span class="wo-drawer__slip-qty">× {{ item.quantity }}</span>
                    <button
                      v-if="hasSlipDetails(item)"
                      type="button"
                      class="wo-drawer__slip-toggle"
                      :aria-expanded="isSlipItemExpanded(idx)"
                      :aria-label="isSlipItemExpanded(idx) ? 'Inklappen' : 'Uitklappen'"
                      @click.stop="toggleSlipItem(idx)"
                    >
                      <BaseIcon
                        name="chevron"
                        size="12"
                        :rotate="isSlipItemExpanded(idx) ? 180 : 0"
                      />
                    </button>
                  </div>

                  <template v-if="isSlipItemExpanded(idx)">
                    <!-- Description -->
                    <p v-if="item.description" class="wo-drawer__slip-desc">
                      {{ item.description }}
                    </p>

                    <!-- Specifications grid -->
                    <div v-if="item.specifications?.length" class="wo-drawer__slip-specs">
                      <div
                        v-for="(spec, si) in item.specifications"
                        :key="si"
                        class="wo-drawer__slip-spec"
                      >
                        <span class="wo-drawer__slip-spec-label">{{ spec.label }}</span>
                        <span class="wo-drawer__slip-spec-value">{{ spec.value }}</span>
                      </div>
                    </div>

                    <!-- Extra attributes (any other key/value pairs not already shown) -->
                    <div
                      v-if="item.attributes?.length"
                      class="wo-drawer__slip-specs wo-drawer__slip-specs--attrs"
                    >
                      <div
                        v-for="(attr, ai) in item.attributes"
                        :key="ai"
                        class="wo-drawer__slip-spec"
                      >
                        <span class="wo-drawer__slip-spec-label">
                          {{ attr.name || attr.label }}
                        </span>
                        <span class="wo-drawer__slip-spec-value">{{ attr.value }}</span>
                      </div>
                    </div>

                    <!-- Notes -->
                    <p v-if="item.notes" class="wo-drawer__slip-notes">{{ item.notes }}</p>
                  </template>
                </div>
                <p v-if="!detail.packingSlipData.value.items?.length" class="wo-drawer__empty-text">
                  Geen items
                </p>
              </template>
              <p v-else class="wo-drawer__empty-text">Pakbon is nog niet actief.</p>
            </div>
          </div>

          <!-- Materieel -->
          <div class="wo-drawer__accordion">
            <button
              class="wo-drawer__accordion-hd"
              @click="sections.resources = !sections.resources"
            >
              <div class="wo-drawer__accordion-title">
                <BaseIcon name="cubes" size="15" />
                <span>{{ $t('resources.requirement.title') }}</span>
              </div>
              <BaseIcon
                name="chevron"
                size="12"
                :rotate="sections.resources ? 180 : 0"
                class="wo-drawer__accordion-chevron"
              />
            </button>
            <div class="wo-drawer__accordion-bd" v-if="sections.resources && workOrder">
              <WorkOrderResourcesCard :work-order="workOrder" :readonly="installerMode" compact />
            </div>
          </div>

          <!-- Situatiefoto's -->
          <div class="wo-drawer__accordion" v-if="situationPhotos.length">
            <button
              class="wo-drawer__accordion-hd"
              @click="sections.situationPhotos = !sections.situationPhotos"
            >
              <div class="wo-drawer__accordion-title">
                <BaseIcon name="image" size="15" />
                <span>Situatiefoto's</span>
              </div>
              <BaseIcon
                name="chevron"
                size="12"
                :rotate="sections.situationPhotos ? 180 : 0"
                class="wo-drawer__accordion-chevron"
              />
            </button>
            <div class="wo-drawer__accordion-bd" v-if="sections.situationPhotos">
              <SituationPhotos :photos="situationPhotos" :editable="false" :compact="true" />
            </div>
          </div>

          <!-- Foto's -->
          <div class="wo-drawer__accordion">
            <button class="wo-drawer__accordion-hd" @click="sections.photos = !sections.photos">
              <div class="wo-drawer__accordion-title">
                <BaseIcon name="image" size="15" />
                <span>Foto's</span>
              </div>
              <BaseIcon
                name="chevron"
                size="12"
                :rotate="sections.photos ? 180 : 0"
                class="wo-drawer__accordion-chevron"
              />
            </button>
            <div class="wo-drawer__accordion-bd" v-if="sections.photos">
              <p class="wo-drawer__photo-phase">Voor montage</p>
              <div class="wo-drawer__photo-grid">
                <div
                  v-for="(photo, idx) in detail.photos.value.before"
                  :key="'b-' + idx"
                  class="wo-drawer__photo-thumb"
                >
                  <img :src="photo.url" @click="detail.openPhotoPreview(photo)" />
                </div>
                <label class="wo-drawer__photo-add" v-if="!detail.photosUploading.value.before">
                  <BaseIcon name="plus" size="18" />
                  <!-- No capture attribute → native iOS/Android picker offers camera + gallery -->
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    @change="(e) => detail.onPhotoUpload(e, 'before')"
                    style="display: none"
                  />
                </label>
              </div>

              <p class="wo-drawer__photo-phase">Na montage</p>
              <div class="wo-drawer__photo-grid">
                <div
                  v-for="(photo, idx) in detail.photos.value.after"
                  :key="'a-' + idx"
                  class="wo-drawer__photo-thumb"
                >
                  <img :src="photo.url" @click="detail.openPhotoPreview(photo)" />
                </div>
                <label class="wo-drawer__photo-add" v-if="!detail.photosUploading.value.after">
                  <BaseIcon name="plus" size="18" />
                  <!-- No capture attribute → native iOS/Android picker offers camera + gallery -->
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    @change="(e) => detail.onPhotoUpload(e, 'after')"
                    style="display: none"
                  />
                </label>
              </div>
            </div>
          </div>

          <!-- Aftekenen -->
          <div class="wo-drawer__accordion">
            <button class="wo-drawer__accordion-hd" @click="sections.signoff = !sections.signoff">
              <div class="wo-drawer__accordion-title">
                <BaseIcon name="check-circle" size="15" />
                <span>Aftekenen</span>
              </div>
              <BaseIcon
                name="chevron"
                size="12"
                :rotate="sections.signoff ? 180 : 0"
                class="wo-drawer__accordion-chevron"
              />
            </button>
            <div class="wo-drawer__accordion-bd" v-if="sections.signoff">
              <template v-if="workOrder.status !== 'completed'">
                <div class="wo-drawer__signoff">
                  <div v-if="detail.productOutcomes.value.length" class="wo-drawer__outcomes">
                    <p class="wo-drawer__outcomes-hint">{{ $t('work_orders.outcomes.hint') }}</p>
                    <div
                      v-for="outcome in detail.productOutcomes.value"
                      :key="outcome.index"
                      class="wo-drawer__outcome"
                    >
                      <div class="wo-drawer__outcome-row">
                        <span class="wo-drawer__outcome-label">{{ outcome.label }}</span>
                        <div class="wo-drawer__outcome-toggle">
                          <button
                            type="button"
                            class="wo-drawer__outcome-btn wo-drawer__outcome-btn--ok"
                            :class="{
                              'wo-drawer__outcome-btn--active': outcome.status === 'installed',
                            }"
                            @click="detail.setOutcomeStatus(outcome, 'installed')"
                          >
                            {{ $t('work_orders.outcomes.installed') }}
                          </button>
                          <button
                            type="button"
                            class="wo-drawer__outcome-btn wo-drawer__outcome-btn--fail"
                            :class="{
                              'wo-drawer__outcome-btn--active': outcome.status === 'failed',
                            }"
                            @click="detail.setOutcomeStatus(outcome, 'failed')"
                          >
                            {{ $t('work_orders.outcomes.failed') }}
                          </button>
                        </div>
                      </div>
                      <div v-if="outcome.status === 'failed'" class="wo-drawer__outcome-details">
                        <SelectInput
                          :id="`wo_drawer_outcome_reason_${outcome.index}`"
                          :label="$t('work_orders.outcomes.reason_label')"
                          v-model="outcome.reason"
                          :options="detail.outcomeReasonOptions.value"
                        />
                        <TextAreaInput
                          :id="`wo_drawer_outcome_notes_${outcome.index}`"
                          v-model="outcome.notes"
                          :placeholder="$t('work_orders.outcomes.notes_placeholder')"
                          :rows="2"
                        />
                      </div>
                    </div>
                    <div v-if="detail.hasFailedOutcomes.value" class="wo-drawer__outcomes-banner">
                      <BaseIcon name="info" size="14" />
                      <span>
                        {{
                          $t('work_orders.outcomes.follow_up_notice', {
                            count: detail.failedOutcomes.value.length,
                          })
                        }}
                      </span>
                    </div>
                  </div>
                  <TextInput
                    id="wo_drawer_signer"
                    label="Naam ondertekenaar"
                    v-model="detail.signerName.value"
                    :placeholder="detail.customer.value?.name || 'Naam...'"
                  />
                  <SignatureInput
                    id="wo_drawer_sig"
                    label="Handtekening klant"
                    clear-label="Wissen"
                    v-model="detail.customerSignature.value"
                  />
                  <TextAreaInput
                    id="wo_drawer_signoff_notes"
                    label="Opmerkingen"
                    v-model="detail.signoffNotes.value"
                    placeholder="Opmerkingen..."
                    :rows="2"
                  />
                </div>
              </template>
              <div v-else class="wo-drawer__completed-info">
                <img
                  v-if="workOrder.completion_signature"
                  :src="workOrder.completion_signature.signature"
                  class="wo-drawer__sig-img"
                />
                <p v-if="workOrder.completion_notes">{{ workOrder.completion_notes }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="wo-drawer__footer">
          <template v-if="isMeasurementWorkOrder && sourceProposalId">
            <p v-if="isRemeasure" class="wo-drawer__remeasure-notice">
              {{ $t('work_orders.measurement_already_definitive') }}
            </p>
            <Btn
              variant="primary"
              size="md"
              icon="ruler"
              :loading="loadingDefinitive"
              class="wo-drawer__footer-btn"
              @click="handleMakeDefinitive"
            >
              {{ $t('proposals.make_definitive') }}
            </Btn>
          </template>
          <div v-if="workOrder.status === 'completed'" class="wo-drawer__done-banner">
            <BaseIcon name="check-circle" size="16" />
            <span>Werkbon afgerond</span>
          </div>
          <Btn
            v-if="detail.canShowPaymentLink.value"
            variant="outlined"
            size="md"
            icon="elements_payment"
            :loading="detail.loadingPaymentLink.value"
            class="wo-drawer__footer-btn"
            @click="detail.showPaymentLink"
          >
            {{ $t('mollie.work_order_complete.show_payment_link') }}
          </Btn>
          <Btn
            v-else-if="workOrder.status !== 'completed'"
            variant="primary"
            size="md"
            @click="detail.completeWorkOrder"
            :loading="detail.completingWorkOrder.value"
            :disabled="!detail.customerSignature.value || detail.outcomesIncomplete.value"
            class="wo-drawer__footer-btn"
          >
            {{
              detail.hasFailedOutcomes.value
                ? $t('work_orders.outcomes.complete_with_exceptions')
                : 'Werkbon afronden'
            }}
          </Btn>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
  import {
    defineComponent,
    ref,
    reactive,
    computed,
    onMounted,
    watch,
    getCurrentInstance,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { useWorkOrderDetail } from '@/composables/useWorkOrderDetail';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { workOrderService } from '@/services/workOrderService';
  import {
    getWorkOrderTitle,
    projectLinkFromWorkOrder,
    salespersonFromWorkOrder,
  } from '@/utils/workOrders';
  import { pickMeasurementSourceProposal } from '@/utils/measurementProposal';
  import { PROPOSAL_STAGES } from '@/constants/proposalTypes';
  import { dealService } from '@/services/dealService';
  import { crmService } from '@/services/crmService';
  import Btn from '@/components/partials/Btn.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import SignatureInput from '@shared/components/inputs/SignatureInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import SituationPhotos from '@/components/partials/SituationPhotos.vue';
  import MessageElement from '@/components/partials/Message.vue';
  import WorkOrderResourcesCard from '@/components/resources/WorkOrderResourcesCard.vue';

  export default defineComponent({
    name: 'WorkOrderDetailDrawer',

    components: {
      Btn,
      TextInput,
      WorkOrderResourcesCard,
      SituationPhotos,
      TextAreaInput,
      SelectInput,
      SignatureInput,
      BaseIcon,
      LoadingSpinner,
      MessageElement,
    },

    props: {
      show: { type: Boolean, default: false },
      workOrder: { type: Object, required: true },
      onUpdate: { type: Function, default: () => {} },
      installerMode: { type: Boolean, default: false },
    },

    emits: ['close', 'schedule'],

    setup(props, { emit }) {
      const isVisible = ref(false);
      onMounted(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            isVisible.value = props.show;
          });
        });
      });
      watch(
        () => props.show,
        (val) => {
          isVisible.value = val;
        }
      );

      const hydratedWorkOrder = ref(null);
      const workOrderRef = computed(() => hydratedWorkOrder.value || props.workOrder);

      let hydrateRequestId = 0;
      const normalizeWorkOrderResponse = (response) => {
        const body = response?.data || response;
        return body?.workOrder || body?.work_order || body;
      };

      const hydrateWorkOrder = async () => {
        const id = props.workOrder?.id;
        if (!id) return null;

        const requestId = ++hydrateRequestId;
        hydratedWorkOrder.value = null;

        try {
          const response = await workOrderService.getWorkOrderById(id);
          const nextWorkOrder = normalizeWorkOrderResponse(response);
          if (requestId === hydrateRequestId && nextWorkOrder) {
            hydratedWorkOrder.value = nextWorkOrder;
          }
          return nextWorkOrder;
        } catch (error) {
          console.error('Failed to enrich work order:', error);
          return null;
        }
      };

      const detail = useWorkOrderDetail(workOrderRef, {
        onUpdate: async (updates) => {
          await props.onUpdate?.(updates);
          if (hydratedWorkOrder.value) {
            hydratedWorkOrder.value = { ...hydratedWorkOrder.value, ...updates };
          }
        },
        onComplete: () => emit('close'),
        onReplace: (updated) => {
          hydratedWorkOrder.value = { ...workOrderRef.value, ...updated };
        },
      });

      // Load pakbon on mount
      onMounted(async () => {
        await hydrateWorkOrder();
        detail.togglePackingSlip(true);
      });

      watch(
        () => props.workOrder?.id,
        async (id, previousId) => {
          if (id && previousId && id !== previousId) {
            await hydrateWorkOrder();
          }
        }
      );

      const sections = reactive({
        activities: true,
        situationPhotos: true,
        pakbon: false,
        resources: false,
        photos: false,
        signoff: false,
      });

      // Per-item expand/collapse for the pakbon. Items are expanded by
      // default; we only track items the user explicitly collapsed.
      const collapsedSlipItems = reactive({});

      const hasSlipDetails = (item) =>
        !!(
          item?.description ||
          item?.notes ||
          item?.specifications?.length ||
          item?.attributes?.length
        );

      const isSlipItemExpanded = (idx) => !collapsedSlipItems[idx];

      const toggleSlipItem = (idx) => {
        collapsedSlipItems[idx] = !collapsedSlipItems[idx];
      };

      const situationPhotos = computed(() => {
        return workOrderRef.value?.project?.situation_photos || [];
      });

      const endTime = computed(() => {
        const dt = detail.appointmentDate.value;
        if (!dt || !workOrderRef.value?.appointment_duration_minutes) return null;
        const end = new Date(
          new Date(dt).getTime() + workOrderRef.value.appointment_duration_minutes * 60000
        );
        return detail.formatTime(end);
      });

      const customerPhone = computed(
        () =>
          workOrderRef.value?.project?.lead?.phone ||
          workOrderRef.value?.project?.lead?.mobile ||
          null
      );

      const call = () => {
        if (customerPhone.value) window.open(`tel:${customerPhone.value}`);
      };

      const handleStartWorkOrder = async () => {
        await detail.startWorkOrder();
      };

      const navigate = () => {
        if (detail.navigationLink.value) {
          window.open(detail.navigationLink.value, '_blank');
        }
      };

      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      // Measurement → definitief maken (first measurement or re-measurement)
      const router = useRouter();

      const isMeasurementWorkOrder = computed(
        () => workOrderRef.value?.work_order_type === 'measurement'
      );

      const dealId = computed(() => workOrderRef.value?.project?.deal_id || null);
      const sourceProposal = ref(null);
      const revisionIntent = ref('create_definitive_from_proforma');
      const isRemeasure = ref(false);
      const needsRemeasureConfirm = ref(false);
      const sourceProposalId = computed(() => sourceProposal.value?.id || null);

      const fetchSourceProposal = async () => {
        const id = dealId.value;
        if (!id || !isMeasurementWorkOrder.value) {
          sourceProposal.value = null;
          isRemeasure.value = false;
          needsRemeasureConfirm.value = false;
          return;
        }
        try {
          const { proposals } = await dealService.getDealProposals(id);
          const picked = pickMeasurementSourceProposal(proposals);
          sourceProposal.value = picked?.proposal || null;
          revisionIntent.value = picked?.intent || 'create_definitive_from_proforma';
          isRemeasure.value = !!picked?.isRemeasure;
          needsRemeasureConfirm.value = !!picked?.needsConfirm;
        } catch {
          sourceProposal.value = null;
          isRemeasure.value = false;
          needsRemeasureConfirm.value = false;
        }
      };

      watch(
        [isMeasurementWorkOrder, dealId],
        ([isMeasurement, id]) => {
          if (isMeasurement && id) {
            fetchSourceProposal();
          } else {
            sourceProposal.value = null;
            isRemeasure.value = false;
            needsRemeasureConfirm.value = false;
          }
        },
        { immediate: true }
      );

      const userStore = useUserStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const loadingDefinitive = ref(false);

      // iOS Safari blocks window.open after an await. A same-tab navigation
      // still counts as the tap, so phones go there instead of a new tab.
      const navigateToReviseUrl = (url) => {
        const sameTab = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 1024;
        if (sameTab) {
          window.location.assign(url);
          return;
        }
        const popup = window.open(url, '_blank');
        if (!popup) window.location.assign(url);
      };

      const openDefinitiveConfigurator = async () => {
        if (!sourceProposalId.value) return;
        loadingDefinitive.value = true;
        try {
          const returnUrl = `${window.location.origin}/proposals/${sourceProposalId.value}`;
          const response = await crmService.getProposalReviseUrl(
            sourceProposalId.value,
            returnUrl,
            revisionIntent.value
          );
          if (response.revise_url) {
            const userId = userStore.user?.id || '';
            const url =
              response.revise_url +
              `&mode=extended&proposal_stage=${PROPOSAL_STAGES.MEASURED}` +
              `&revised_by=${userId}&revised_from=work_order`;
            navigateToReviseUrl(url);
          }
        } catch (error) {
          const data = error?.response?.data || {};
          if (error?.response?.status === 409 && data.code === 'PROPOSAL_HAS_SUCCESSOR') {
            toastStore.addToast({
              type: 'error',
              message: $t('proposals.revise_has_successor_error'),
            });
          } else {
            router.push(`/proposals/${sourceProposalId.value}`);
          }
        } finally {
          loadingDefinitive.value = false;
        }
      };

      const handleMakeDefinitive = async () => {
        if (!sourceProposalId.value) return;
        if (needsRemeasureConfirm.value) {
          const modalId = uiStore.addModal(false, {
            title: $t('work_orders.remeasure_confirm_title'),
            description: $t('work_orders.remeasure_confirm_description'),
            status: 'warning',
            submitText: $t('work_orders.remeasure_confirm_submit'),
            cancelText: $t('common.cancel'),
            onSubmit: () => {
              uiStore.closeModal(modalId);
              openDefinitiveConfigurator();
            },
            onCancel: () => uiStore.closeModal(modalId),
          });
          return;
        }
        await openDefinitiveConfigurator();
      };

      // ── Annuleren (alleen admins, desktop) ─────────────────────────────
      // Escape hatch om een per ongeluk aangemaakte/ingeplande werkbon snel
      // terug te draaien zonder de detailpagina te openen.
      const isAdmin = computed(() =>
        ['admin', 'super_admin', 'owner'].includes(userStore.currentOrganization?.role)
      );

      const canCancelWorkOrder = computed(
        () => isAdmin.value && !['completed', 'cancelled'].includes(workOrderRef.value?.status)
      );

      const cancellingWorkOrder = ref(false);

      const handleCancelWorkOrder = () => {
        const wo = workOrderRef.value;
        if (!wo?.id) return;

        const label = wo.work_order_number ? `werkbon ${wo.work_order_number}` : 'deze werkbon';
        const modalId = uiStore.addModal(false, {
          title: 'Werkbon annuleren?',
          description: `Weet je zeker dat je ${label} wilt annuleren? De werkbon verdwijnt uit de planning maar blijft zichtbaar met de status Geannuleerd.`,
          submitText: 'Ja, annuleer',
          cancelText: 'Terug',
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            cancellingWorkOrder.value = true;
            try {
              await workOrderService.updateWorkOrderStatus(wo.id, 'cancelled');
              if (hydratedWorkOrder.value) {
                hydratedWorkOrder.value = { ...hydratedWorkOrder.value, status: 'cancelled' };
              }
              toastStore.addToast({ type: 'success', message: 'Werkbon geannuleerd' });
              // Parent laten syncen (lijst/kalender); de statuswijziging zelf
              // is hierboven al via de status-endpoint doorgevoerd.
              await props.onUpdate?.({ status: 'cancelled' });
              emit('close');
            } catch (error) {
              console.error('Failed to cancel work order:', error);
              toastStore.addToast({ type: 'error', message: 'Kon werkbon niet annuleren' });
            } finally {
              cancellingWorkOrder.value = false;
            }
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      // ── Type wijzigen (alleen admins) ──────────────────────────────────
      const workOrderTypes = ['installation', 'measurement', 'service'];
      const canChangeType = computed(
        () => isAdmin.value && !['completed', 'cancelled'].includes(workOrderRef.value?.status)
      );
      const changingType = ref(false);

      const saveType = async (type, confirmProjectInMeasurement = false) => {
        const wo = workOrderRef.value;
        changingType.value = true;
        try {
          await workOrderService.updateWorkOrder(wo.id, {
            work_order_type: type,
            ...(confirmProjectInMeasurement ? { confirm_project_in_measurement: true } : {}),
          });
          hydratedWorkOrder.value = { ...wo, work_order_type: type };
          toastStore.addToast({ type: 'success', message: $t('work_orders.type_changed') });
          await props.onUpdate?.({ work_order_type: type });
        } catch (error) {
          if (error?.response?.data?.error === 'project_in_measurement') {
            const modalId = uiStore.addModal(false, {
              title: $t('work_orders.type_in_measurement_title'),
              description: $t('work_orders.type_in_measurement_description'),
              submitText: $t('work_orders.type_in_measurement_confirm'),
              cancelText: $t('common.cancel'),
              onSubmit: () => {
                uiStore.closeModal(modalId);
                saveType(type, true);
              },
              onCancel: () => uiStore.closeModal(modalId),
            });
            return;
          }
          console.error('Failed to change work order type:', error);
          toastStore.addToast({ type: 'error', message: $t('work_orders.type_change_failed') });
        } finally {
          changingType.value = false;
        }
      };

      const handleTypeChange = (event) => {
        const type = event.target.value;
        // Keep showing the saved type until the change is stored.
        event.target.value = workOrderRef.value?.work_order_type || 'installation';
        saveType(type);
      };

      // Swipe to close
      const touchStartY = ref(0);
      const onTouchStart = (e) => {
        touchStartY.value = e.touches[0].clientY;
      };
      const onTouchMove = (e) => {
        if (e.touches[0].clientY - touchStartY.value > 80) emit('close');
      };

      const drawerTitle = computed(
        () => getWorkOrderTitle(workOrderRef.value) || workOrderRef.value?.work_order_number
      );

      const linkedProject = computed(() => projectLinkFromWorkOrder(workOrderRef.value));
      const salesperson = computed(() => salespersonFromWorkOrder(workOrderRef.value));
      const canOpenProject = computed(
        () => (!props.installerMode || isAdmin.value) && !!linkedProject.value
      );

      return {
        workOrder: workOrderRef,
        drawerTitle,
        linkedProject,
        salesperson,
        isVisible,
        detail,
        sections,
        situationPhotos,
        endTime,
        customerPhone,
        call,
        navigate,
        handleStartWorkOrder,
        isMeasurementWorkOrder,
        dealId,
        sourceProposalId,
        isRemeasure,
        loadingDefinitive,
        handleMakeDefinitive,
        isAdmin,
        canOpenProject,
        canCancelWorkOrder,
        cancellingWorkOrder,
        handleCancelWorkOrder,
        workOrderTypes,
        canChangeType,
        changingType,
        handleTypeChange,
        onTouchStart,
        onTouchMove,
        hasSlipDetails,
        isSlipItemExpanded,
        toggleSlipItem,
      };
    },
  });
</script>

<style scoped lang="scss">
  .wo-drawer {
    position: fixed;
    inset: 0;
    // Above the mobile tab bar (1000), below modals (1300) and toasts (2000).
    z-index: 1100;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;

    &--open {
      pointer-events: all;
      opacity: 1;
    }

    &__overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      cursor: pointer;
    }

    // ── Panel ──────────────────────────────────────────
    &__panel {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 90vw;
      max-width: 480px;
      background: var(--slate-50);
      box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
      transform: translateX(100%);
      transition: transform 0.3s ease;

      @include sm-down {
        top: auto;
        left: 0;
        right: 0;
        width: 100%;
        max-width: none;
        // Definite height: Safari will not shrink the scroll body when the
        // sheet is only capped with max-height, which pushes the footer
        // (Werkbon afronden) below the visible sheet.
        height: 95dvh;
        max-height: 95dvh;
        border-radius: 1rem 1rem 0 0;
        transform: translateY(100%);
      }
    }

    &--open &__panel {
      transform: translateX(0);
      @include sm-down {
        transform: translateY(0);
      }
    }

    // ── Handle ─────────────────────────────────────────
    &__handle {
      display: none;
      justify-content: center;
      padding: 0.625rem 0 0.25rem;
      cursor: grab;
      flex-shrink: 0;

      @include sm-down {
        display: flex;
      }
    }

    &__handle-bar {
      width: 36px;
      height: 4px;
      background: var(--slate-300);
      border-radius: 2px;
    }

    // ── Header ─────────────────────────────────────────
    &__header {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.75rem 1.25rem;
    }

    &__back {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-600);

      @include hover {
        color: var(--slate-900);
      }
    }

    &__header-right {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &__edit-link {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--brand-600);
      text-decoration: none;

      @include hover {
        color: var(--brand-700);
        text-decoration: underline;
      }
    }

    // Annuleren is een desktop-correctie voor admins; op mobiel (monteurs-
    // context) verbergen we hem zodat hij niet per ongeluk wordt geraakt.
    &__cancel-link {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      padding: 0;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--red-500);

      &:disabled {
        opacity: 0.5;
        cursor: default;
      }

      @include hover {
        color: var(--red-700);
        text-decoration: underline;
      }
    }

    &__status {
      padding: 0.2rem 0.625rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 500;

      &--pending {
        background: var(--slate-100);
        color: var(--slate-600);
      }
      &--scheduled {
        background: var(--blue-100);
        color: var(--blue-700);
      }
      &--ready_for_installation {
        background: var(--green-100);
        color: var(--green-700);
      }
      &--in_progress {
        background: var(--amber-100);
        color: var(--amber-700);
      }
      &--completed {
        background: var(--green-100);
        color: var(--green-700);
      }
      &--cancelled {
        background: var(--red-100);
        color: var(--red-700);
      }
    }

    // ── Title block ────────────────────────────────────
    &__title-block {
      flex-shrink: 0;
      padding: 0 1.25rem 1rem;
    }

    &__wo-number-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__wo-number {
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-500);
    }

    &__type-badge {
      display: inline-flex;
      padding: 1px 6px;
      border-radius: 0.25rem;
      font-size: 0.625rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;

      &--measurement {
        background: var(--amber-100);
        color: var(--amber-700);
      }

      &--service {
        background: var(--purple-100, var(--slate-100));
        color: var(--purple-700, var(--slate-700));
      }

      &--installation {
        background: var(--slate-100);
        color: var(--slate-700);
      }
    }

    &__type-select {
      appearance: none;
      border: none;
      padding-right: 16px;
      font-family: inherit;
      cursor: pointer;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3E%3Cpath d='M1 3l3 3 3-3' fill='none' stroke='%2364748b' stroke-width='1.2'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 4px center;

      &:disabled {
        opacity: 0.6;
        cursor: wait;
      }

      &:focus-visible {
        outline: 2px solid var(--brand-500);
        outline-offset: 1px;
      }
    }

    &__project-title {
      font-size: 1.25rem;
      font-weight: 700;
      color: var(--slate-900);
      margin: 0.125rem 0 0.625rem;
    }

    &__quick-actions {
      display: flex;
      gap: 0.5rem;
    }

    // ── Body ───────────────────────────────────────────
    &__body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    &__notes {
      margin: 0.75rem 1.25rem 0;
      white-space: pre-wrap;
    }

    &__notes-edit {
      margin: 0.75rem 1.25rem 0;
    }

    // ── Map ────────────────────────────────────────────
    &__map {
      position: relative;
      overflow: hidden;

      iframe {
        display: block;
        pointer-events: none;
      }
    }

    &__map-chip {
      position: absolute;
      bottom: 0.5rem;
      right: 0.5rem;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      background: white;
      border: 1px solid var(--slate-200);
      border-radius: 0.375rem;
      padding: 0.2rem 0.5rem;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-700);
      text-decoration: none;
      @include shadow('xs');

      &:hover {
        background: var(--slate-50);
      }
    }

    // ── Address ────────────────────────────────────────
    &__address {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.75rem 1.25rem;
      background: white;
      border-bottom: 1px solid var(--slate-200);
    }

    &__address-left {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: var(--slate-500);
    }

    &__address-street {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__address-city {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__nav-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.3rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: white;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-700);
      text-decoration: none;

      @include hover {
        background: var(--slate-50);
        border-color: var(--slate-300);
      }
    }

    // ── Info list ──────────────────────────────────────
    &__info-list {
      background: white;
      border-bottom: 1px solid var(--slate-200);
    }

    &__info-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.625rem 1.25rem;
      font-size: 0.875rem;

      & + & {
        border-top: 1px solid var(--slate-100);
      }
    }

    &__info-label {
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--slate-400);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    &__info-value {
      font-weight: 500;
      color: var(--slate-900);
      text-align: right;

      &--with-action {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
      }
    }

    &__info-link {
      color: var(--brand-600);
      text-decoration: none;
      font-weight: 500;

      @include hover {
        text-decoration: underline;
      }
    }

    // ── Accordion ──────────────────────────────────────
    &__accordion {
      background: white;
      border-bottom: 1px solid var(--slate-200);
    }

    &__accordion-hd {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 0.875rem 1.25rem;
      background: none;
      border: none;
      cursor: pointer;
      text-align: left;

      @include hover {
        background: var(--slate-50);
      }
    }

    &__accordion-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__accordion-right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__accordion-chevron {
      color: var(--slate-400);
      transition: transform 0.2s;
    }

    &__accordion-bd {
      padding: 0 1.25rem 1rem;
    }

    &__inline-btn {
      background: none;
      border: 1px solid var(--slate-200);
      border-radius: 0.375rem;
      padding: 0.15rem 0.6rem;
      font-size: 0.75rem;
      color: var(--slate-600);
      cursor: pointer;

      &:hover {
        background: var(--slate-50);
        color: var(--slate-900);
      }
    }

    // ── Activities ─────────────────────────────────────
    &__act-list {
      display: flex;
      flex-direction: column;
    }

    &__act-item {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      padding: 0.625rem 0;
      border-bottom: 1px solid var(--slate-50);

      &--done .wo-drawer__act-name {
        text-decoration: line-through;
        color: var(--slate-400);
      }
    }

    &__act-check {
      accent-color: var(--brand-600);
      flex-shrink: 0;
      width: 1.125rem;
      height: 1.125rem;
    }

    &__act-name {
      flex: 1;
      font-size: 0.9375rem;
      font-weight: 500;
      color: var(--slate-800);
      cursor: pointer;
    }

    &__act-input {
      width: 100%;
      border: 1px solid var(--brand-300);
      border-radius: 0.25rem;
      padding: 0.2rem 0.4rem;
      font-size: 0.9375rem;
      outline: none;
    }

    // ── Pakbon slip items ──────────────────────────────
    &__slip-item {
      padding: 0.75rem 0;
      border-bottom: 1px solid var(--slate-100);
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      &:last-child {
        border-bottom: none;
      }

      &--done {
        .wo-drawer__slip-label {
          text-decoration: line-through;
          color: var(--slate-400);
        }
        opacity: 0.65;
      }
    }

    &__slip-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__slip-check {
      accent-color: var(--brand-600);
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      cursor: pointer;
    }

    &__slip-label {
      flex: 1;
      color: var(--slate-900);
      font-weight: 600;
      font-size: 0.9375rem;
      cursor: pointer;
      user-select: none;
    }

    &__slip-qty {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-500);
      background: var(--slate-100);
      padding: 2px 8px;
      border-radius: 6px;
      flex-shrink: 0;
    }

    &__slip-toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      background: none;
      border: none;
      border-radius: 0.25rem;
      color: var(--slate-400);
      cursor: pointer;
      flex-shrink: 0;

      svg {
        transition: transform 0.2s;
      }

      @include hover {
        background: var(--slate-100);
        color: var(--slate-700);
      }
    }

    &__slip-desc {
      margin: 0 0 0 1.625rem;
      font-size: 0.8125rem;
      color: var(--slate-500);
      line-height: 1.4;
    }

    &__slip-specs {
      margin-left: 1.625rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-100);
      border-radius: 0.5rem;
      overflow: hidden;

      &--attrs {
        margin-top: 0;
        border-top: none;
        border-radius: 0 0 0.5rem 0.5rem;
      }
    }

    &__slip-spec {
      display: flex;
      gap: 0.75rem;
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      border-bottom: 1px solid var(--slate-100);

      &:last-child {
        border-bottom: none;
      }
    }

    &__slip-spec-label {
      color: var(--slate-400);
      min-width: 90px;
      flex-shrink: 0;
      font-weight: 500;
    }

    &__slip-spec-value {
      color: var(--slate-800);
      font-weight: 500;
      flex: 1;
    }

    &__slip-notes {
      margin: 0 0 0 1.625rem;
      font-size: 0.75rem;
      color: var(--slate-400);
      font-style: italic;
    }

    // ── Photos ─────────────────────────────────────────
    &__photo-phase {
      margin: 0.5rem 0 0.375rem;
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--slate-400);
    }

    &__photo-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
    }

    &__photo-thumb {
      width: 3.5rem;
      height: 3.5rem;
      border-radius: 0.375rem;
      overflow: hidden;
      border: 1px solid var(--slate-200);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        cursor: pointer;
      }
    }

    &__photo-add {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 3.5rem;
      height: 3.5rem;
      border: 1.5px dashed var(--slate-300);
      border-radius: 0.375rem;
      color: var(--slate-400);
      cursor: pointer;

      @include hover {
        border-color: var(--brand-400);
        color: var(--brand-600);
        background: var(--brand-50);
      }
    }

    // ── Sign-off ───────────────────────────────────────
    &__signoff {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    // ── Product outcomes (afronden met uitzonderingen) ──
    &__outcomes {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__outcomes-hint {
      font-size: 0.8125rem;
      color: var(--slate-500);
      margin: 0;
    }

    &__outcome {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: var(--slate-50);
    }

    &__outcome-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
    }

    &__outcome-label {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-700);
      @include ellipsis(2);
    }

    &__outcome-toggle {
      display: flex;
      flex-shrink: 0;
      border: 1px solid var(--slate-200);
      border-radius: 0.375rem;
      overflow: hidden;
      background: white;
    }

    &__outcome-btn {
      border: none;
      background: none;
      padding: 0.375rem 0.625rem;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-500);
      cursor: pointer;

      &--ok.wo-drawer__outcome-btn--active {
        background: var(--green-100);
        color: var(--green-700);
      }

      &--fail.wo-drawer__outcome-btn--active {
        background: var(--red-100);
        color: var(--red-700);
      }
    }

    &__outcome-details {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__outcomes-banner {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      border-radius: 0.5rem;
      background: var(--amber-50);
      border: 1px solid var(--amber-200);
      color: var(--amber-800);
      font-size: 0.8125rem;

      svg {
        flex-shrink: 0;
        margin-top: 2px;
      }
    }

    &__completed-info {
      p {
        font-size: 0.875rem;
        color: var(--slate-600);
        margin: 0.5rem 0 0;
      }
    }

    &__sig-img {
      max-width: 100%;
      border: 1px solid var(--slate-200);
      border-radius: 0.375rem;
    }

    // ── Footer ─────────────────────────────────────────
    &__footer {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.75rem 1.25rem;
      padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
      background: white;
      border-top: 1px solid var(--slate-200);
    }

    &__footer-btn {
      flex: 1;
    }

    &__remeasure-notice {
      margin: 0;
      padding: 0.5rem 0.625rem;
      border-radius: 0.5rem;
      background: var(--amber-50);
      border: 1px solid var(--amber-200);
      color: var(--amber-800);
      font-size: 0.75rem;
      line-height: 1.4;
    }

    &__done-banner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      color: var(--green-700);
      font-weight: 600;
      font-size: 0.9rem;
      flex: 1;
    }

    // ── Shared ─────────────────────────────────────────
    &__loading {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 0;
      font-size: 0.875rem;
      color: var(--slate-500);
    }

    &__empty {
      padding: 1rem 0;
      text-align: center;
      color: var(--slate-400);
      font-size: 0.875rem;

      p {
        margin: 0;
      }
    }

    &__empty-action {
      margin-top: 0.375rem;
      background: none;
      border: none;
      color: var(--brand-600);
      font-size: 0.8125rem;
      cursor: pointer;
      text-decoration: underline;
    }

    &__empty-text {
      font-size: 0.8125rem;
      color: var(--slate-400);
      margin: 0;
    }
  }
</style>
